import React from "react";
import { Space, Select } from "antd";
import { DropdownArrowOptions } from "antd/lib/dropdown/dropdown";
import { InputfieldTypes } from "../../../../../../enums/InputFieldAttributesEnums";
import TNButton from "../../../../../../reusable-components/TNButton";
import {
  ButtonShape,
  ButtonSize,
  ButtonType,
} from "../../../../../../enums/ButtonAttributesEnums";
import { Material } from "../../../../../../context/materialListingContext";
import { getPrevDollorFormattedString } from "../../../../../../Utilities";
import ConditionalRender from "../../../../../../reusable-components/ConditionalRender";
import styles from "./AddMaterialsForm.module.scss";
import { thousandsSeparator } from "../../../../../../Utilities/regex";
import { formatToFixedTwo } from "../../../../../../utils/numberFormatterUtil";

interface AddMaterialProps {
  materialCategoriesOptions: DropdownArrowOptions[];
  materialTypeCategoriesOptions: DropdownArrowOptions[];
  materialNameDropdownOptions: DropdownArrowOptions[];
  quarryDropdownOptions?: DropdownArrowOptions[];
  supplyStatus?: DropdownArrowOptions[];
  testingStatus?: DropdownArrowOptions[];
  washedStatus?: DropdownArrowOptions[];
}
export const CONSTANTS_MATERIAL_DATA = {
  NO_DATA: "No orders have been added yet",
  ADD_TRUCK_BTN_TXT: "Add Trucking Order",
  ADD_TRUCK_DETAILS_TXT: "Add Trucking Details",
  WIDTH: "89%",
  HEIGHT: "100vh",
  VENDORS_FOUND: "Vendors found",
  NO_VENDORS_FOUND: "No vendors found",
  FIND_VENDORS_BTN: "Find Nearest Vendors",
  MODAL: {
    HEADER: "Confirm Changes",
    CONTENT: "Are you sure you want to update the material tax status?",
    OK_BUTTON_LABEL: "Confirm",
  },
};
export const drawerMaterialHeaderStyles = {
  backgroundColor: "#586370",
  padding: "1rem",
  paddingLeft: "2.75rem",
  color: "white",
};
export const getAddMaterialFields = ({
  materialCategoriesOptions,
  materialTypeCategoriesOptions,
  materialNameDropdownOptions,
  quarryDropdownOptions,
  supplyStatus,
  testingStatus,
  washedStatus,
}: AddMaterialProps) => {
  return [
    {
      name: "materialCategories",
      type: InputfieldTypes.SELECT,
      label: "Material Categories",
      required: true,
      placeholder: "Select",
      width: "1/3",
      options: materialCategoriesOptions,
    },
    {
      name: "materialType",
      type: InputfieldTypes.SELECT,
      label: "Material Type",
      required: false,
      placeholder: "Select",
      width: "1/3",
      options: materialTypeCategoriesOptions,
    },
    {
      name: "materialName",
      type: InputfieldTypes.SELECT,
      label: "Material Name",
      required: false,
      placeholder: "Select",
      width: "1/3",
      options: materialNameDropdownOptions,
    },
    {
      name: "testingStatus",
      type: InputfieldTypes.SELECT,
      label: "Testing Status",
      required: false,
      placeholder: "Search",
      width: "1/3",
      disabled: false,
      options: testingStatus,
    },
    {
      name: "washedStatus",
      type: InputfieldTypes.SELECT,
      label: "Washed Status",
      required: false,
      disabled: false,
      placeholder: "Select",
      width: "1/3",
      options: washedStatus,
    },
    {
      name: "supply",
      type: InputfieldTypes.SELECT,
      label: "Supply",
      required: false,
      placeholder: "Select",
      disabled: false,
      width: "1/3",
      options: supplyStatus,
    },
    {
      name: "quarryName",
      type: InputfieldTypes.SELECT,
      label: "Quarry Name",
      required: false,
      placeholder: "Select",
      width: "1/3",
      options: quarryDropdownOptions,
    },
    {
      name: "searchRadius",
      type: InputfieldTypes.TEXT,
      label: "Search Radius",
      required: true,
      placeholder: "Enter radius",
      width: "1/3",
    },
  ];
};

export const testingStatus = [
  { label: "Does Not Pass Spec", value: "Does Not Pass Spec" },
  { label: "Passes Spec", value: "Passes Spec" },
  { label: "Unknown", value: "Unknown" },
];

export const washedStatus = [
  { label: "Unknown", value: "Unknown" },
  { label: "Unwashed", value: "Unwashed" },
  { label: "Washed", value: "Washed" },
];

export const supplyStatus = [
  { label: "Average Supply", value: "Average Supply" },
  { label: "High Supply", value: "High Supply" },
  { label: "Low Supply", value: "Low Supply" },
  { label: "No Supply", value: "No Supply" },
  { label: "Unknown Supply", value: "Unknown Supply" },
];

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
}

const options = [
  {
    value: "Manufactured",
    label: "Manufactured",
  },
  {
    value: "Natural",
    label: "Natural",
  },
];
interface ColumnsProps {
  handleSelectVendor?: (props: Material) => void;
  handleTaxChange?: (props: Material) => void;
}
export const getColumns: ColumnsType<DataType> = ({
  handleSelectVendor,
  handleTaxChange,
}: ColumnsProps) => {
  return [
    {
      title: "Vendor Name",
      dataIndex: "vendorName",
      key: "vendorName",
    },
    {
      title: "Distance & Time",
      dataIndex: "distanceTime",
      key: "distanceTime",
    },
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
    },
    {
      title: "Material Tax",
      dataIndex: "materialTax",
      key: "materialTax",
      width: "10rem",
      align: "left",
      render: (value, record) => (
        <>
          <ConditionalRender
            visible={value !== "Natural" && value !== "Manufactured"}
            fallback={value}
          >
            <Select
              bordered={false}
              options={options}
              placeholder={<a>Unknown</a>}
              className={styles.MaterialTaxSelect}
              onChange={(val) => handleTaxChange(record, val)}
            />
          </ConditionalRender>
        </>
      ),
    },
    {
      title: "Cost/Unit",
      dataIndex: "costPerTon",
      key: "costPerTon",
      render: (value, record) =>
        `$ ${thousandsSeparator(formatToFixedTwo(String(value)?.replace(/\$/g, "")?.trim()))} / ${record?.quantityType}`,
    },
    {
      title: "Last used",
      dataIndex: "lastUsed",
      key: "lastUsed",
    },
    {
      title: "",
      key: "action",
      render: (_, record) => {
        const handleClick = () => {
          handleSelectVendor({
            vendor: _?.vendorName,
            materialCost: _?.costPerTon,
            materialName: `${_?.category} - ${_?.materialLabel}`,
            materialSelected: _?.materialSelected,
            quantityType: _?.quantityType,
            key: _?.key,
            pickUpLat: _?.pickUpLat,
            pickUpLng: _?.pickUpLng,
            category: _?.category,
            subCategory: _?.subCategory,
            material: _?.materialLabel,
            subShortName: _?.subShortName,
            materialTax: _?.materialTax,
          });
        };
        return (
          <Space size="middle">
            <TNButton
              text="Select Vendor"
              type={ButtonType.PRIMARY}
              shape={ButtonShape.DEFAULT}
              size={ButtonSize.LARGE}
              onClick={handleClick}
              disabled={
                record?.materialTax !== "Natural" &&
                record?.materialTax !== "Manufactured"
              }
            />
          </Space>
        );
      },
    },
  ];
};

export const handleMilesFeets = (value?: string) => {
  return value.endsWith("mi")
    ? value.replace("mi", "miles")
    : value.endsWith("ft")
      ? value.replace("ft", "feet")
      : value;
};

export const fieldNames = {
  MATERIAL_NAME: "materialName",
  MATERIAL_TYPE: "materialType",
  MATERIAL_CATEGORIES: "materialCategories",
  QUARRY: "quarryName",
};
